import React, { useMemo, useRef, useState } from 'react';
import { bookingBrandFontFamilyVar, teboxDesignSystem } from '../../styles/tebox-booking';
import { formatMoneyFromCents } from '../../utils/format-money';

type BrandColors = {
  textColor?: string;
  buttonColor?: string;
  primaryTextColor?: string;
  borderRadius?: string;
  borderColor?: string;
  backgroundColor?: string;
};

const TOOLTIP_HOVER_DELAY_MS = 500;

function DevPaymentInfoTooltip({ brandColors }: { brandColors?: BrandColors }) {
  const [open, setOpen] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);

  const clearHoverTimer = () => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const showTooltip = () => {
    clearHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => setOpen(true), TOOLTIP_HOVER_DELAY_MS);
  };

  const hideTooltip = () => {
    clearHoverTimer();
    setOpen(false);
  };

  return (
    <span
      className="relative inline-flex shrink-0"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      <button
        type="button"
        aria-label="Test payment info"
        aria-describedby={open ? 'shop-dev-payment-tooltip' : undefined}
        className="inline-flex items-center justify-center"
        style={{
          width: 22,
          height: 22,
          padding: 0,
          border: `1px solid ${brandColors?.borderColor ?? '#D8DBE0'}`,
          borderRadius: '999px',
          backgroundColor: brandColors?.backgroundColor ?? '#FFFFFF',
          color: brandColors?.textColor ?? '#404040',
          cursor: 'help',
          fontFamily: bookingBrandFontFamilyVar,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        i
      </button>
      {open ? (
        <div
          id="shop-dev-payment-tooltip"
          role="tooltip"
          className="absolute left-1/2 bottom-full z-[1600] mb-2 w-64 -translate-x-1/2 rounded-[6px] border p-3 shadow-sm"
          style={{
            fontFamily: bookingBrandFontFamilyVar,
            backgroundColor: brandColors?.backgroundColor ?? '#FFFFFF',
            borderColor: brandColors?.borderColor ?? '#D8DBE0',
            boxShadow: teboxDesignSystem.shadows.sm,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: teboxDesignSystem.typography.caption.fontSize,
              fontWeight: 500,
              color: brandColors?.textColor,
            }}
          >
            Test payment
          </p>
          <p
            style={{
              margin: '6px 0 0',
              fontSize: teboxDesignSystem.typography.caption.fontSize,
              lineHeight: 1.4,
              color: brandColors?.textColor,
              opacity: 0.85,
            }}
          >
            Stripe Connect is not configured yet. Use any test card — default {TEST_CARD_NUMBER}{' '}
            works.
          </p>
        </div>
      ) : null}
    </span>
  );
}

type Props = {
  amountCents: number;
  intentId: string;
  onSuccess: (result: { paymentIntentId: string }) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  brandColors?: BrandColors;
};

const TEST_CARD_NUMBER = '4242424242424242';

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function formatCardNumber(value: string): string {
  const digits = digitsOnly(value).slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

function formatExpiry(value: string): string {
  const digits = digitsOnly(value).slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

export function ShopDevPaymentStep({
  amountCents,
  intentId,
  onSuccess,
  onError,
  disabled,
  brandColors,
}: Props) {
  const [cardNumber, setCardNumber] = useState(TEST_CARD_NUMBER);
  const [expiry, setExpiry] = useState('12/34');
  const [cvc, setCvc] = useState('123');
  const [submitting, setSubmitting] = useState(false);

  const inputStyle = useMemo<React.CSSProperties>(
    () => ({
      fontFamily: bookingBrandFontFamilyVar,
      color: brandColors?.textColor,
      backgroundColor: brandColors?.backgroundColor ?? '#FFFFFF',
      border: `1px solid ${brandColors?.borderColor ?? '#D8DBE0'}`,
      borderRadius: brandColors?.borderRadius ?? teboxDesignSystem.borderRadius.md,
      width: '100%',
      padding: '10px 12px',
      boxSizing: 'border-box',
    }),
    [brandColors],
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled || submitting) return;

    const normalizedCard = digitsOnly(cardNumber);
    const normalizedExpiry = digitsOnly(expiry);
    const normalizedCvc = digitsOnly(cvc);

    if (normalizedCard.length < 13) {
      onError('Enter a valid test card number.');
      return;
    }
    if (normalizedExpiry.length !== 4) {
      onError('Enter expiry as MM/YY.');
      return;
    }
    if (normalizedCvc.length < 3) {
      onError('Enter a valid CVC.');
      return;
    }

    setSubmitting(true);
    try {
      onSuccess({ paymentIntentId: intentId });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="flex items-center gap-2">
        <p
          style={{
            fontFamily: bookingBrandFontFamilyVar,
            fontWeight: 500,
            color: brandColors?.textColor,
            margin: 0,
          }}
        >
          {formatMoneyFromCents(amountCents)} due now
        </p>
        <DevPaymentInfoTooltip brandColors={brandColors} />
      </div>

      <label className="flex flex-col gap-1">
        <span
          style={{
            fontFamily: bookingBrandFontFamilyVar,
            fontSize: teboxDesignSystem.typography.caption.fontSize,
            color: brandColors?.textColor,
          }}
        >
          Card number
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="cc-number"
          value={formatCardNumber(cardNumber)}
          onChange={(e) => setCardNumber(digitsOnly(e.target.value))}
          style={inputStyle}
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span
            style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: teboxDesignSystem.typography.caption.fontSize,
              color: brandColors?.textColor,
            }}
          >
            Expiry
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp"
            value={expiry}
            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
            placeholder="MM/YY"
            style={inputStyle}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span
            style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: teboxDesignSystem.typography.caption.fontSize,
              color: brandColors?.textColor,
            }}
          >
            CVC
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-csc"
            value={cvc}
            onChange={(e) => setCvc(digitsOnly(e.target.value).slice(0, 4))}
            style={inputStyle}
          />
        </label>
      </div>

      <button
        type="submit"
        disabled={disabled || submitting}
        className="w-full py-3 px-6 font-medium disabled:opacity-50"
        style={{
          fontFamily: bookingBrandFontFamilyVar,
          borderRadius: brandColors?.borderRadius ?? '6px',
          backgroundColor: brandColors?.buttonColor ?? '#021B1B',
          color: brandColors?.primaryTextColor ?? '#FFFFFF',
          border: 'none',
          cursor: disabled || submitting ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? 'Processing…' : 'Complete test payment'}
      </button>
    </form>
  );
}

export default ShopDevPaymentStep;
